<template>
    <div class="uta-audio-player">
        <audio
            v-show="status != 'error'"
            ref="player"
            controls
            preload="metadata"
            :src="playableSrc"
            :style="{ height: height, width: width }"
            @loadedmetadata="onReady"
            @canplay="onReady"
            @playing="onReady"
            @progress="onProgress"
            @error="onError"
        ></audio>
        <div
            v-if="status == 'loading' && !timedOut"
            class="uta-audio-player-status caption grey--text"
        >
            <v-progress-circular
                indeterminate
                color="success"
                :size="14"
                :width="2"
                class="mr-2"
            ></v-progress-circular>
            Loading recording...
        </div>
        <div v-if="status == 'error' || timedOut" class="uta-audio-player-status">
            <span class="caption text-danger mr-2">{{ statusMessage }}</span>
            <v-btn
                small
                outline
                color="success"
                class="ma-0 uta-audio-player-btn"
                @click="retry"
            >
                <v-icon small class="mr-1">mdi-refresh</v-icon>
                Retry
            </v-btn>
            <v-btn
                small
                outline
                color="success"
                class="ma-0 ml-2 uta-audio-player-btn"
                :href="src"
                target="_blank"
                rel="noopener"
            >
                <v-icon small class="mr-1">mdi-open-in-new</v-icon>
                Open
            </v-btn>
        </div>
    </div>
</template>
<script>
const LOAD_TIMEOUT_MS = 10000;

export default {
    name: "audio-player",
    props: {
        src: String,
        height: { type: String, default: "35px" },
        width: { type: String, default: "300px" },
    },
    data() {
        return {
            status: "loading",
            attempt: 0,
            timedOut: false,
            errorText: "",
            loadTimer: null,
        };
    },
    computed: {
        playableSrc() {
            if (!this.src) return "";
            if (!this.attempt) return this.src;

            // A fresh query string forces the browser to refetch instead of
            // reusing the failed response from its cache.
            const separator = this.src.indexOf("?") == -1 ? "?" : "&";

            return this.src + separator + "retry=" + this.attempt;
        },
        statusMessage() {
            if (this.errorText) return this.errorText;

            return "Recording is taking longer than usual to load.";
        },
    },
    watch: {
        src() {
            this.attempt = 0;
            this.load();
        },
    },
    mounted() {
        this.load();
    },
    beforeDestroy() {
        this.clearTimer();
    },
    methods: {
        clearTimer() {
            if (!this.loadTimer) return;

            clearTimeout(this.loadTimer);
            this.loadTimer = null;
        },
        startTimer() {
            this.clearTimer();

            this.loadTimer = setTimeout(() => {
                if (this.status == "loading") this.timedOut = true;
            }, LOAD_TIMEOUT_MS);
        },
        load() {
            this.clearTimer();
            this.status = "loading";
            this.timedOut = false;
            this.errorText = "";

            if (!this.src) return;

            this.$nextTick(() => {
                const player = this.$refs.player;

                if (!player) return;

                if (player.readyState >= 1) {
                    this.onReady();

                    return;
                }

                player.load();
                this.startTimer();
            });
        },
        retry() {
            this.attempt += 1;
            this.load();
        },
        onReady() {
            this.clearTimer();
            this.status = "ready";
            this.timedOut = false;
            this.errorText = "";
        },
        onProgress() {
            // Data is still arriving, so give the download more time before
            // telling the user it looks stuck.
            if (this.status == "loading" && !this.timedOut) this.startTimer();
        },
        onError() {
            this.clearTimer();
            this.status = "error";
            this.errorText = this.describeError();
        },
        describeError() {
            const player = this.$refs.player;
            const error = player && player.error;
            const code = error && error.code;

            if (code == 2) return "Network error while loading the recording.";
            if (code == 3) return "This recording could not be played.";
            if (code == 4)
                return "The recording could not be loaded. It may be missing or blocked.";

            return "The recording could not be loaded.";
        },
    },
};
</script>
<style>
.uta-audio-player-status {
    display: flex;
    align-items: center;
    margin-top: 2px;
}
.uta-audio-player-btn {
    min-width: 0;
    padding: 0 8px;
}
</style>
